import movMonedaService from '../services/movMonedaService';
import { toLocalDateInputValue } from './facturaVentaFechas';

export interface ConversionPrecioPedido {
  precioOriginal: number;
  codigoMoneda: number;
  cotizacion: number;
  precioEnPesos: number;
}

export interface PrecioPedidoConvertido {
  precio: number;
  codigoMoneda: number;
  conversion: ConversionPrecioPedido | null;
}

const round2 = (value: number): number => Math.round(value * 100) / 100;

export const resolverCodigoMonedaProducto = (producto: unknown): number => {
  if (!producto || typeof producto !== 'object') return 0;
  const data = producto as Record<string, unknown>;
  const values = new Map(Object.keys(data).map((key) => [key.toLowerCase(), data[key]]));
  const candidates = [
    values.get('tipomoneda'),
    values.get('tipomonedaarticulo'),
    values.get('codigomoneda'),
    values.get('codmoneda'),
  ];
  for (const candidate of candidates) {
    if (candidate == null || candidate === '') continue;
    const value =
      typeof candidate === 'object'
        ? (candidate as Record<string, unknown>).codigoMoneda ??
          (candidate as Record<string, unknown>).CodigoMoneda ??
          (candidate as Record<string, unknown>).codigo
        : candidate;
    const codigo = Number(value);
    if (Number.isFinite(codigo)) return codigo;
  }
  return 0;
};

export const convertirPrecioPedidoAPesos = (
  precio: number,
  codigoMoneda: number,
  cotizacion?: number | null,
): number => {
  const precioNumerico = Number(precio);
  if (!Number.isFinite(precioNumerico)) {
    throw new Error('El precio del producto no es válido.');
  }
  if (codigoMoneda === 0) return round2(precioNumerico);

  const cotizacionNumerica = Number(cotizacion);
  if (!Number.isFinite(cotizacionNumerica) || cotizacionNumerica <= 0) {
    throw new Error(`No hay cotización válida para la moneda ${codigoMoneda}.`);
  }
  return round2(precioNumerico * cotizacionNumerica);
};

export const resolverConversionPrecioPedido = async (
  precio: number,
  codigoMoneda: number,
  fechaEmision: Date | string,
): Promise<ConversionPrecioPedido | null> => {
  if (codigoMoneda === 0) return null;

  const fechaCotizacion =
    typeof fechaEmision === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fechaEmision)
      ? fechaEmision
      : toLocalDateInputValue(fechaEmision);
  if (!fechaCotizacion) {
    throw new Error('La fecha de emisión del pedido no es válida para cotizar el producto.');
  }

  let cotizacion: number | null = null;
  try {
    const delDia = await movMonedaService.getCotizacionDia(codigoMoneda, fechaCotizacion);
    if (delDia.cotizacion > 0) cotizacion = delDia.cotizacion;
  } catch {
    /* sin cotización del día */
  }
  if (cotizacion == null || !(cotizacion > 0)) {
    try {
      cotizacion = (await movMonedaService.getMovMoneda(codigoMoneda, fechaCotizacion)).cotizacion;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 404) {
        throw new Error(
          `No se pudo obtener la cotización de la moneda ${codigoMoneda} para el ${fechaCotizacion}.`,
        );
      }
      try {
        cotizacion = await movMonedaService.getUltimaCotizacionPorMoneda(codigoMoneda);
      } catch {
        throw new Error(`No se pudo obtener la cotización de la moneda ${codigoMoneda}.`);
      }
    }
  }

  return {
    precioOriginal: Number(precio),
    codigoMoneda,
    cotizacion: Number(cotizacion),
    precioEnPesos: convertirPrecioPedidoAPesos(precio, codigoMoneda, cotizacion),
  };
};

export const resolverPrecioPedidoEnPesos = async (
  precio: number,
  codigoMoneda: number,
  fechaEmision: Date | string,
): Promise<number> => {
  if (codigoMoneda === 0) return convertirPrecioPedidoAPesos(precio, codigoMoneda);
  const conversion = await resolverConversionPrecioPedido(precio, codigoMoneda, fechaEmision);
  if (!conversion) throw new Error(`No hay conversión disponible para la moneda ${codigoMoneda}.`);
  return conversion.precioEnPesos;
};

export const resolverPrecioPedidoConMoneda = async (
  precio: number,
  producto: unknown,
  codigoMonedaLista: number | null,
  fechaEmision: Date | string,
): Promise<PrecioPedidoConvertido> => {
  const codigoMoneda = codigoMonedaLista ?? resolverCodigoMonedaProducto(producto);
  if (codigoMoneda === 0) {
    return { precio: convertirPrecioPedidoAPesos(precio, 0), codigoMoneda, conversion: null };
  }

  const conversion = await resolverConversionPrecioPedido(precio, codigoMoneda, fechaEmision);
  if (!conversion) {
    throw new Error(`No hay conversión disponible para la moneda ${codigoMoneda}.`);
  }

  return { precio: conversion.precioEnPesos, codigoMoneda, conversion };
};
